import { Box, Heading, Text, VStack, HStack, Badge, useColorModeValue } from '@chakra-ui/react';
import { format, isSameDay } from 'date-fns';
import { CalendarActionItem } from '../useCalendarActionItems';
import { CalendarMilestone } from '../useCalendarMilestones';

interface CalendarDayViewProps {
  currentDate: Date;
  actionItems: CalendarActionItem[];
  milestones: CalendarMilestone[];
}

/**
 * Calendar Day View Component
 * Displays a single day with detailed action items and milestones
 */
export function CalendarDayView({
  currentDate,
  actionItems,
  milestones,
}: CalendarDayViewProps) {
  const dayActionItems = actionItems.filter((actionItem) => {
    if (!actionItem.dueDate) return false;
    const dueDate = actionItem.dueDate instanceof Date ? actionItem.dueDate : new Date(actionItem.dueDate);
    return isSameDay(dueDate, currentDate);
  });

  const dayMilestones = milestones.filter((milestone) => {
    if (!milestone.dueDate) return false;
    const dueDate = milestone.dueDate instanceof Date ? milestone.dueDate : new Date(milestone.dueDate);
    return isSameDay(dueDate, currentDate);
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const actionItemBg = useColorModeValue('green.50', 'green.900');
  const actionItemBorder = useColorModeValue('green.200', 'green.700');
  const actionItemCompleteBg = useColorModeValue('gray.50', 'gray.700');
  const actionItemCompleteBorder = useColorModeValue('gray.200', 'gray.600');
  const milestoneBg = useColorModeValue('purple.50', 'purple.900');
  const milestoneBorder = useColorModeValue('purple.200', 'purple.700');
  const milestoneCompleteBg = useColorModeValue('gray.50', 'gray.700');
  const milestoneCompleteBorder = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const emptyTextColor = useColorModeValue('gray.500', 'gray.400');
  const completeTextColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box bg={bgColor} borderRadius="lg" borderWidth={1} borderColor={borderColor} p={6}>
      <Heading size="lg" mb={6}>
        {format(currentDate, 'EEEE, MMMM d, yyyy')}
      </Heading>

      {/* Action Items Section */}
      <Box mb={8}>
        <HStack mb={3} align="center" gap={2}>
          <Text fontSize="lg" fontWeight="semibold">
            📋 Action Items
          </Text>
          <Badge colorScheme="green">{dayActionItems.length}</Badge>
        </HStack>
        <VStack spacing={2} align="stretch">
          {dayActionItems.length === 0 ? (
            <Text color={emptyTextColor} fontStyle="italic" py={4}>
              No action items for this day
            </Text>
          ) : (
            dayActionItems.map((actionItem) => {
              const isComplete = actionItem.complete;
              const bg = isComplete ? actionItemCompleteBg : actionItemBg;
              const border = isComplete ? actionItemCompleteBorder : actionItemBorder;

              return (
                <Box
                  key={actionItem.id}
                  borderWidth={1}
                  p={4}
                  borderRadius="lg"
                  bg={bg}
                  borderColor={border}
                  cursor="pointer"
                  _hover={{ opacity: 0.8 }}
                >
                  <Text
                    fontWeight="medium"
                    textDecoration={isComplete ? 'line-through' : 'none'}
                    color={isComplete ? completeTextColor : undefined}
                    mb={1}
                  >
                    {actionItem.description}
                  </Text>
                  <HStack spacing={3} fontSize="sm" color={textColor} mt={1}>
                    {actionItem.owner && <Text>Owner: {actionItem.owner.name}</Text>}
                    {actionItem.linkedRock && <Text>Rock: {actionItem.linkedRock.rock}</Text>}
                    {isComplete && (
                      <Badge colorScheme="green" size="sm">
                        ✓ Completed
                      </Badge>
                    )}
                  </HStack>
                </Box>
              );
            })
          )}
        </VStack>
      </Box>

      {/* Milestones Section */}
      <Box>
        <HStack mb={3} align="center" gap={2}>
          <Text fontSize="lg" fontWeight="semibold">
            🎯 Milestones
          </Text>
          <Badge colorScheme="purple">{dayMilestones.length}</Badge>
        </HStack>
        <VStack spacing={2} align="stretch">
          {dayMilestones.length === 0 ? (
            <Text color={emptyTextColor} fontStyle="italic" py={4}>
              No milestones for this day
            </Text>
          ) : (
            dayMilestones.map((milestone) => {
              const isComplete = milestone.complete;
              const bg = isComplete ? milestoneCompleteBg : milestoneBg;
              const border = isComplete ? milestoneCompleteBorder : milestoneBorder;

              return (
                <Box
                  key={milestone.id}
                  borderWidth={1}
                  p={4}
                  borderRadius="lg"
                  bg={bg}
                  borderColor={border}
                  cursor="pointer"
                  _hover={{ opacity: 0.8 }}
                >
                  <Text
                    fontWeight="medium"
                    textDecoration={isComplete ? 'line-through' : 'none'}
                    color={isComplete ? completeTextColor : undefined}
                    mb={1}
                  >
                    {milestone.task}
                  </Text>
                  <HStack spacing={3} fontSize="sm" color={textColor} mt={1}>
                    {milestone.rock && <Text>Rock: {milestone.rock.rock}</Text>}
                    {milestone.rock?.owner && <Text>Owner: {milestone.rock.owner.name}</Text>}
                    {isComplete && (
                      <Badge colorScheme="green" size="sm">
                        ✓ Completed
                      </Badge>
                    )}
                  </HStack>
                </Box>
              );
            })
          )}
        </VStack>
      </Box>
    </Box>
  );
}

